import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Dashboard/Header/Header';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Clock,
  AlertCircle,
  MoreVertical,
  CheckCircle2,
  Search,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  X,
  Calendar,
  IndianRupee,
  Briefcase,
  SlidersHorizontal as Filters,
  LayoutDashboard
} from 'lucide-react';
import styles from './Invoices.module.css';
import Footer from '../../../components/Common/Footer/Footer';
import NoData from '../../../components/Common/NoData/NoData';
import Pagination from '../../../components/Common/Pagination/Pagination';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import { useLoading } from '../../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const Invoices = () => {
  const navigate = useNavigate();
  const mainContentRef = useRef(null);
  const isInitialMount = useRef(true);

  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Data State
  const [invoices, setInvoices] = useState([]);
  const [statsData, setStatsData] = useState({
    total_paid: 0,
    pending_amount: 0,
    pending_count: 0,
    overdue_amount: 0,
    trends: { paid: 0 },
    progress: { paid: 0, pending: 0, overdue: 0 }
  });

  // Pagination & Filtering State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    customer_id: '',
    project_id: '',
    status: ''
  });
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    customers: []
  });

  useEffect(() => {
    fetchDropdownData();
    fetchStatistics();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  // Handle scroll to top on page change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const scrollTimeout = setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
    return () => clearTimeout(scrollTimeout);
  }, [currentPage]);

  // Handle click outside action menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles['action-wrapper']}`)) {
        setOpenMenuIndex(null);
      }
    };

    const handleScroll = () => {
      if (openMenuIndex !== null) setOpenMenuIndex(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuIndex]);

  const [isTableLoading, setIsTableLoading] = useState(true);

  const fetchDropdownData = async () => {
    try {
      const [projectsRes, customersRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/customers-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        customers: customersRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/sales-invoices/statistics');
      if (response.data.success) {
        setStatsData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchInvoices = async () => {
    setIsTableLoading(true);
    try {
      const response = await api.get('/sales-invoices', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        },
        skipLoader: true
      });
      if (response.data.success) {
        setInvoices(response.data.data.data || []);
        setPaginationData({
          total: response.data.data.total,
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page
        });
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load sales invoices');
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      customer_id: '',
      project_id: '',
      status: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const toggleActionMenu = (e, index, invoice) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.right - 180
      });
      setOpenMenuIndex(index);
      setSelectedInvoice(invoice);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Invoice</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Sales Invoice?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete invoice <strong>#${invoice.invoice_number}</strong>?<br/>
          This action will permanently remove the record from the dashboard.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/sales-invoices/${invoice.id}`);
          if (response.data.success) {
            toast.success(response.data.message || 'Sales Invoice deleted successfully');
            fetchInvoices();
            fetchStatistics();
          }
        } catch (error) {
          console.error('Error deleting invoice:', error);
          toast.error(error.response?.data?.message || 'Failed to delete invoice');
        }
      }
    });
  };

  const stats = [
    {
      label: 'Total Paid',
      value: `₹${parseFloat(statsData.total_paid || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      trend: `${statsData.trends?.paid >= 0 ? '+' : ''}${statsData.trends?.paid}%`,
      isPositive: (statsData.trends?.paid || 0) >= 0,
      icon: <Wallet size={24} />,
      type: 'paid',
      progress: statsData.progress?.paid || 0,
      color: '#10b981'
    },
    {
      label: 'Pending Approval',
      value: `₹${parseFloat(statsData.pending_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      trend: `${statsData.pending_count || 0} Invoices`,
      isPositive: true,
      icon: <Clock size={24} />,
      type: 'pending',
      progress: statsData.progress?.pending || 0,
      color: '#f59e0b'
    },
    {
      label: 'Overdue Total',
      value: `₹${parseFloat(statsData.overdue_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      trend: `${statsData.progress?.overdue || 0}% of Total`,
      isPositive: false,
      icon: <AlertCircle size={24} />,
      type: 'overdue',
      progress: statsData.progress?.overdue || 0,
      color: '#ef4444'
    }
  ];

  return (
    <div className={styles['invoices-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Billing Overview</h1>
              <p>Manage, track and generate professional sales invoices for your enterprise.</p>
            </div>
            <button className={styles['add-btn']} onClick={() => navigate('/invoices/add')}>
              <Plus size={20} />
              <span>Create New Sales Invoice</span>
            </button>
          </div>

          {/* Metrics Summary */}
          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {stats[0]?.value} Received · {stats[1]?.value} Pending
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {stats.map((stat, index) => (
                <div key={index} className={`${styles['metric-card']} ${styles[stat.type]}`}>
                  <div className={styles['metric-icon-box']}>
                    {stat.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{stat.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{stat.value}</span>
                      <div className={`${styles['metric-trend']} ${stat.isPositive ? styles['positive'] : styles['negative']}`}>
                        {stat.isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        {stat.trend}
                      </div>
                    </div>
                  </div>
                  <div className={styles['progress-bar-container']}>
                    <div
                      className={styles['progress-bar']}
                      style={{ width: `${stat.progress}%`, background: stat.color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Controls */}
          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search Invoice # or Client Name..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.project_id || filters.customer_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Sales Invoices</h3>
                  <p>Refine the list by customer, project, or invoice status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Customer</label>
                  <CustomSelect
                    value={filters.customer_id ? {
                      value: filters.customer_id,
                      label: filterData.customers.find(c => c.id === filters.customer_id)?.customer_name
                    } : null}
                    onChange={(opt) => handleFilterChange('customer_id', opt ? opt.value : '')}
                    options={filterData.customers.map(c => ({ value: c.id, label: c.customer_name }))}
                    placeholder="All Customers"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt ? opt.value : '')}
                    options={filterData.projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1).replace('_', ' ')
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'partial_paid', label: 'Partial Paid' },
                      { value: 'paid', label: 'Paid' },
                      { value: 'overdue', label: 'Overdue' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          {/* Table Content */}
          <div className={styles['custom-table-container']}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>INVOICE NUMBER</th>
                    <th>CLIENT / PROJECT</th>
                    <th>DATE</th>
                    <th>TOTAL AMOUNT</th>
                    <th>PAID</th>
                    <th>BALANCE</th>
                    <th>STATUS</th>
                    <th style={{ textAlign: 'center' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {isTableLoading ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '100px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                          <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                          <span style={{ color: '#64748b', fontWeight: 600 }}>Syncing financial data...</span>
                        </div>
                      </td>
                    </tr>
                  ) : invoices.length > 0 ? (
                    invoices.map((invoice, index) => {
                      const total = parseFloat(invoice.total_amount) || 0;
                      const paid = (invoice.payments_received || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                      const balance = total - paid;

                      return (
                        <tr key={invoice.id || index}>
                          <td>
                            <span
                              className={styles['invoice-id']}
                              onClick={() => navigate(`/invoices/view/${invoice.id}`)}
                            >
                              #{invoice.invoice_number}
                            </span>
                          </td>
                          <td>
                            <div className={styles['client-cell']}>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span >{invoice.project?.project_name || 'N/A'}</span>
                              </div>
                            </div>
                          </td>
                          <td>{new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className={styles['amount-cell']}>{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td style={{ color: '#10b981', fontWeight: 600 }}>{paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className={`${styles['balance-cell']} ${balance > 0 ? styles['has-balance'] : ''}`}>
                            {balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td>
                            <span className={`${styles['status-pill']} ${styles[`status-${invoice.status}`]}`}>
                              <span className={styles['status-dot']}></span>
                              {invoice.status?.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <div className={styles['action-wrapper']}>
                              <button
                                className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                                onClick={(e) => toggleActionMenu(e, index, invoice)}
                              >
                                <MoreVertical size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <NoData message="No sales invoices found matching your criteria." colSpan={8} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className={styles['invoice-cards']}>
              {isTableLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f97316' }} />
                </div>
              ) : invoices.length > 0 ? (
                invoices.map((invoice, index) => {
                  const total = parseFloat(invoice.total_amount) || 0;
                  const paid = (invoice.payments_received || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                  const balance = total - paid;

                  return (
                    <div key={invoice.id} className={styles['invoice-card']}>
                      <div className={styles['card-main']}>
                        <div className={styles['card-info']}>
                          <span className={styles['card-id']} onClick={() => navigate(`/invoices/view/${invoice.id}`)}>#{invoice.invoice_number}</span>
                          <span className={styles['card-amount']}>{total.toLocaleString('en-IN')}</span>
                        </div>
                        <div className={styles['action-wrapper']}>
                          <button
                            className={styles['action-btn']}
                            onClick={(e) => toggleActionMenu(e, index, invoice)}
                          >
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </div>
                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Client / Project</span>
                          <span className={styles['detail-value']}>
                            {invoice.project?.project_name || 'N/A'}
                          </span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Balance</span>
                          <span className={`${styles['detail-value']} ${balance > 0 ? styles['has-balance'] : ''}`}>{balance.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                      <div className={styles['card-footer']}>
                        <div className={styles['footer-left']}>
                          <Calendar size={14} />
                          <span>{new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                        </div>
                        <div className={styles['footer-statuses']}>
                          <span className={`${styles['status-pill']} ${styles[`status-${invoice.status}`]}`}>
                            {invoice.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <NoData message="No sales invoices found." isTable={false} />
              )}
            </div>

            {/* Desktop Pagination */}
            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          {/* Mobile Pagination */}
          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          <Footer />
        </main>
      </div >

      {/* Action Menu Backdrop */}
      {
        openMenuIndex !== null && (
          <div
            className={styles['action-dropdown'] + ' ' + styles['shadow-premium']}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 1000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles['dropdown-item']} onClick={() => navigate(`/invoices/view/${selectedInvoice?.id}`)}>
              <Eye size={16} /> View Details
            </button>
            <button className={styles['dropdown-item']} onClick={() => navigate(`/invoices/edit/${selectedInvoice?.id}`)}>
              <Pencil size={16} /> Edit Invoice
            </button>
            <div className={styles['dropdown-divider']}></div>
            <button className={`${styles['dropdown-item']} ${styles['delete']}`} onClick={() => handleDeleteInvoice(selectedInvoice)}>
              <Trash2 size={16} /> Delete Invoice
            </button>
          </div>
        )
      }
    </div >
  );
};

export default Invoices;
